/** A part of the sheet, top to bottom in CSS px of the unscaled sheet */
interface Span {
  top: number
  bottom: number
}

/** A PDF page: the part of the sheet it shows, under the column heads of a table that runs on from the page before */
interface Slice extends Span {
  head?: HTMLTableSectionElement
}

const MM = 96 / 25.4
const FONT = '"Google Sans Variable", "Noto Sans Thai", sans-serif'

/** The sheet's padding in CSS px: the page's margins */
function margins(sheet: HTMLElement) {
  const style = getComputedStyle(sheet)
  return {
    top: Number.parseFloat(style.paddingTop),
    bottom: Number.parseFloat(style.paddingBottom),
    left: Number.parseFloat(style.paddingLeft),
    right: Number.parseFloat(style.paddingRight),
  }
}

/** Where each of the sheet's elements sits, in the unscaled sheet's px (the sheet is scaled to the screen) */
function measure(sheet: HTMLElement) {
  const frame = sheet.getBoundingClientRect()
  const k = frame.width / sheet.offsetWidth || 1
  return (el: Element): Span => {
    const r = el.getBoundingClientRect()
    return { top: (r.top - frame.top) / k, bottom: (r.bottom - frame.top) / k }
  }
}

/**
 * The pages a sheet prints as, cut where printing cuts: between rows and blocks, never inside a table row or a block
 * that avoids breaking (`break-inside: avoid`), each page as tall as A4 inside the sheet's padding (its margins). From
 * the second page, a table that runs on starts again under its column heads.
 */
function paginate(sheet: HTMLElement, pageHeight: number): Slice[] {
  const { top: padTop, bottom: padBottom } = margins(sheet)
  const span = measure(sheet)
  const solid = [...sheet.querySelectorAll<HTMLElement>('*')]
    .filter(
      (el) =>
        el.tagName === 'TR' ||
        el.tagName === 'IMG' ||
        getComputedStyle(el).breakInside.startsWith('avoid') ||
        (el.childElementCount === 0 && !!el.textContent?.trim()),
    )
    .map(span)
    .filter((b) => b.bottom > b.top)
  const tables = [...sheet.querySelectorAll('table')].map((table) => ({ table: span(table), head: table.tHead }))
  const contentBottom = Math.max(padTop, ...solid.map((b) => b.bottom))
  const room = pageHeight - padTop - padBottom
  const slices: Slice[] = []
  let y = padTop
  while (y < contentBottom - 1) {
    // The column heads of the innermost table this page starts inside, past its heads
    const head = slices.length
      ? tables
          .filter((t) => t.head && t.table.top < y && t.table.bottom > y && span(t.head).bottom <= y)
          .sort((a, b) => b.table.top - a.table.top)[0]?.head
      : undefined
    const headHeight = head ? span(head).bottom - span(head).top : 0
    const limit = y + room - headHeight
    let end = contentBottom
    if (limit < contentBottom) {
      const cuts = solid
        .flatMap((b) => [b.top, b.bottom])
        .filter((c) => c > y + 1 && c <= limit && !solid.some((b) => b.top < c - 0.5 && b.bottom > c + 0.5))
      end = cuts.length ? Math.max(...cuts) : limit
    }
    slices.push({ top: y, bottom: end, head: head ?? undefined })
    y = end
  }
  return slices
}

/**
 * A page of the sheet to draw: a copy off screen without every element wholly above or below the page, except the
 * column heads it repeats. Each page is laid out and drawn on its own, so nothing is cut by position (the drawing's
 * line heights differ from the screen's by a fraction of a px, which adds up down a long sheet). `remove` when drawn.
 */
async function pageCopy(sheet: HTMLElement, slice: Slice) {
  const span = measure(sheet)
  const copy = sheet.cloneNode(true) as HTMLElement
  const copies = [...copy.querySelectorAll('*')]
  for (const [i, el] of [...sheet.querySelectorAll('*')].entries()) {
    if (el.tagName === 'COL' || el.tagName === 'COLGROUP' || slice.head?.contains(el)) continue
    const b = span(el)
    if (b.bottom > b.top && (b.bottom <= slice.top + 0.5 || b.top >= slice.bottom - 0.5)) copies[i]?.remove()
  }
  copy.style.transform = 'none'
  const holder = document.createElement('div')
  holder.style.cssText = 'position: fixed; top: 0; left: -10000px;'
  holder.append(copy)
  document.body.append(holder)
  await Promise.all([...copy.querySelectorAll('img')].map((img) => img.decode().catch(() => undefined)))
  return { copy, remove: () => holder.remove() }
}

/**
 * The sheet (an A4 page at full size, its padding the page's margins) as an A4 PDF that pages as printing does: no row
 * cut in two, a table's column heads again atop each page it runs on to, the document's `PrintRunning` line in the top
 * margin from the second page, and "หน้า n / N" in the bottom one. Each page an image, saved as `name`.
 */
export async function savePdf(sheet: HTMLElement, name: string, pageLabel: string) {
  const [{ toCanvas, getFontEmbedCSS }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')])
  await document.fonts.ready
  const width = sheet.offsetWidth
  const pageHeight = (width * 297) / 210
  const slices = paginate(sheet, pageHeight)
  const { top: padTop, bottom: padBottom, left: padLeft, right: padRight } = margins(sheet)
  const running = sheet.querySelector<HTMLElement>('[data-running-start]')?.dataset
  const fontEmbedCSS = await getFontEmbedCSS(sheet)
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true })
  for (const [n, slice] of slices.entries()) {
    const { copy, remove } = await pageCopy(sheet, slice)
    const page = await toCanvas(copy, { pixelRatio: 2, backgroundColor: '#ffffff', height: Math.ceil(pageHeight), fontEmbedCSS }).finally(
      remove,
    )
    const r = page.width / width
    const g = page.getContext('2d')
    if (!g) continue
    // As the print's page margins say it: 8 pt, grey
    g.font = `${(8 * 96 * r) / 72}px ${FONT}`
    g.fillStyle = '#555555'
    if (n > 0 && running?.runningStart) {
      g.textBaseline = 'bottom'
      g.textAlign = 'left'
      g.fillText(running.runningStart, padLeft * r, (padTop - 2 * MM) * r)
      if (running.runningEnd) {
        g.textAlign = 'right'
        g.fillText(running.runningEnd, (width - padRight) * r, (padTop - 2 * MM) * r)
      }
    }
    g.textBaseline = 'middle'
    g.textAlign = 'right'
    g.fillText(`${pageLabel} ${n + 1} / ${slices.length}`, (width - padRight) * r, (pageHeight - padBottom / 2) * r)
    if (n > 0) pdf.addPage()
    pdf.addImage(page.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297)
  }
  pdf.save(`${name}.pdf`)
}
